"use client";

import {
  createContext,
  forwardRef,
  useContext,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactNode,
} from "react";

type TabsContextValue = {
  value: string;
  onValueChange?: (value: string) => void;
  tabId: (value: string) => string;
  panelId: (value: string) => string;
  listRef: MutableRefObject<HTMLDivElement | null>;
};

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) throw new Error("Tabs components must be used inside Tabs.");
  return context;
}

function slugify(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]+/g, "-");
}

export type TabsProps = HTMLAttributes<HTMLDivElement> & {
  value: string;
  onValueChange?: (value: string) => void;
};

export function Tabs({ value, onValueChange, children, ...props }: TabsProps) {
  const baseId = slugify(useId());
  const listRef = useRef<HTMLDivElement | null>(null);
  const context: TabsContextValue = {
    value,
    onValueChange,
    tabId: (tabValue) => `${baseId}-tab-${slugify(tabValue)}`,
    panelId: (tabValue) => `${baseId}-panel-${slugify(tabValue)}`,
    listRef,
  };

  return (
    <TabsContext.Provider value={context}>
      <div {...props}>{children}</div>
    </TabsContext.Provider>
  );
}

export const TabList = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function TabList({ className = "", ...props }, ref) {
    const { listRef } = useTabsContext();

    return (
      <div
        {...props}
        ref={(element) => {
          listRef.current = element;
          if (typeof ref === "function") ref(element);
          else if (ref) ref.current = element;
        }}
        role="tablist"
        className={`flex items-center border-b border-[var(--fz-tabs-border)] bg-[var(--fz-tabs-bg)] text-[var(--fz-tabs-text)] ${className}`}
      />
    );
  },
);

export type TabProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  value: string;
  children?: ReactNode;
};

export const Tab = forwardRef<HTMLButtonElement, TabProps>(function Tab(
  { value, children, className = "", onClick, onKeyDown, disabled, ...props },
  ref,
) {
  const { value: selectedValue, onValueChange, tabId, panelId, listRef } = useTabsContext();
  const selected = selectedValue === value;
  const tabRef = useRef<HTMLButtonElement | null>(null);

  function moverFoco(event: KeyboardEvent<HTMLButtonElement>) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();

    const tabs = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>(
        'button[role="tab"]:not(:disabled)',
      ) ?? [],
    );
    const atual = tabs.indexOf(tabRef.current as HTMLButtonElement);
    if (atual < 0 || tabs.length === 0) return;

    const proximo = event.key === "Home"
      ? 0
      : event.key === "End"
        ? tabs.length - 1
        : (atual + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
    const tab = tabs[proximo];
    tab.focus();
    onValueChange?.(tab.dataset.fzTabValue ?? "");
  }

  return (
    <button
      {...props}
      ref={(element) => {
        tabRef.current = element;
        if (typeof ref === "function") ref(element);
        else if (ref) ref.current = element;
      }}
      type="button"
      id={tabId(value)}
      role="tab"
      aria-selected={selected}
      aria-controls={panelId(value)}
      tabIndex={selected && !disabled ? 0 : -1}
      disabled={disabled}
      data-fz-tab-value={value}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (!event.defaultPrevented) moverFoco(event);
      }}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented && !disabled) onValueChange?.(value);
      }}
      className={`border-b-2 border-transparent px-1 pb-3 text-sm font-semibold text-[var(--fz-tabs-text)] transition-colors hover:text-[var(--fz-tabs-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fz-tabs-focus-ring)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${selected ? "border-[var(--fz-tabs-active-text)] text-[var(--fz-tabs-active-text)]" : ""} ${className}`}
    >
      {children}
    </button>
  );
});

export type TabPanelProps = HTMLAttributes<HTMLDivElement> & {
  value: string;
  children?: ReactNode;
};

export function TabPanel({ value, children, ...props }: TabPanelProps) {
  const { value: selectedValue, tabId, panelId } = useTabsContext();
  const active = selectedValue === value;

  return (
    <div
      {...props}
      id={panelId(value)}
      role="tabpanel"
      aria-labelledby={tabId(value)}
      hidden={!active}
      tabIndex={0}
    >
      {active ? children : null}
    </div>
  );
}
